import React, { useState, ReactNode } from 'react';
import styled from 'styled-components';
import Button from './Button';

interface WizardProps {
  steps: {
    title: string;
    content: ReactNode;
    optional?: boolean;
  }[];
  onComplete: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialStep?: number;
}

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StepsProgress = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;

  @media (max-width: 768px) {
    padding-bottom: 1rem;
  }
`;

const StepItem = styled.div<{ active: boolean; completed: boolean; optional: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 1rem;
  cursor: pointer;
  opacity: ${({ active, completed }) => (active || completed ? 1 : 0.6)};
  
  &:last-child {
    margin-right: 0;
  }
`;

const StepNumber = styled.div<{ active: boolean; completed: boolean; optional: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${({ active, completed, optional }) => 
    completed ? '#10b981' : active ? '#3b82f6' : optional ? '#d1d5db' : '#e5e7eb'};
  color: ${({ completed, active }) => 
    completed || active ? 'white' : '#6b7280'};
  font-weight: 600;
  margin-right: 0.5rem;
`;

const StepTitle = styled.div<{ active: boolean; completed: boolean }>`
  font-size: 0.875rem;
  font-weight: ${({ active, completed }) => (active || completed ? 600 : 400)};
  color: ${({ active, completed }) => 
    active ? '#1f2937' : completed ? '#10b981' : '#6b7280'};

  @media (max-width: 640px) {
    display: none;
  }
`;

const StepConnector = styled.div<{ completed: boolean }>`
  flex-grow: 1;
  height: 2px;
  background-color: ${({ completed }) => 
    completed ? '#10b981' : '#e5e7eb'};
  margin: 0 0.5rem;
`;

const StepContent = styled.div`
  padding: 1rem 0;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const Wizard: React.FC<WizardProps> = ({
  steps,
  onComplete,
  onCancel,
  isLoading = false,
  initialStep = 0,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  
  const handleNext = () => {
    // Mark current step as completed
    setCompletedSteps(prev => {
      const updated = new Set(prev);
      updated.add(currentStep);
      return updated;
    });
    
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleStepClick = (index: number) => {
    // Only allow navigation to previous steps or completed steps
    if (index < currentStep || completedSteps.has(index)) {
      setCurrentStep(index);
    }
  };
  
  return (
    <WizardContainer>
      <StepsProgress>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <StepItem 
              active={currentStep === index}
              completed={completedSteps.has(index)}
              optional={!!step.optional}
              onClick={() => handleStepClick(index)}
            >
              <StepNumber 
                active={currentStep === index}
                completed={completedSteps.has(index)} 
                optional={!!step.optional}
              >
                {completedSteps.has(index) ? '✓' : index + 1}
              </StepNumber>
              <StepTitle 
                active={currentStep === index}
                completed={completedSteps.has(index)}
              >
                {step.title}
              </StepTitle>
            </StepItem>
            {index < steps.length - 1 && (
              <StepConnector completed={
                Array.from(completedSteps).some(s => s > index)
              } />
            )}
          </React.Fragment>
        ))}
      </StepsProgress>
      
      <StepContent>
        {steps[currentStep].content}
      </StepContent>
      
      <WizardActions>
        <div>
          {!isFirstStep && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          )}
        </div>
        
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
            style={{ marginRight: '0.5rem' }}
          >
            Cancel
          </Button>
          
          <Button
            type="button"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 
              isLastStep ? 'Complete' : 'Next'}
          </Button>
        </div>
      </WizardActions>
    </WizardContainer>
  );
};

export default Wizard; 